import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../../context/CartContext';
import QuickContact from '../contact/QuickContact';
import { useTranslation } from 'react-i18next';

const CartPage = () => {
  const { items: cartItems, updateQuantity, removeFromCart, subtotal } = useCart();
  const [promoCode, setPromoCode] = useState('');
  const { t } = useTranslation('common');

  const removeItem = (id) => removeFromCart(id);
  const shipping = 0; // Free shipping
  const tax = subtotal * 0.08; // 8% tax
  const total = subtotal + shipping + tax;

  return (
    <div className="min-h-screen bg-cream pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-16">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Cart Items Section */}
          <div className="lg:w-2/3">
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h1 className="text-2xl font-bold text-text-dark mb-6 flex items-center">
                <span className="text-gold mr-3">{t('cart.title')}</span>
              </h1>
              <p className="text-text-gray text-sm mb-6">{t('cart.itemsCount', { count: cartItems.length })}</p>

              {cartItems.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-text-gray mb-6">{t('cart.empty')}</p>
                  <Link to="/shop" className="inline-block bg-brown text-cream px-6 py-3 rounded-md text-sm font-medium hover:bg-brown-dark">
                    {t('cart.browse')}
                  </Link>
                </div>
              ) : (
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 p-4 border-b border-gray-100 last:border-b-0">
                    {/* Product Image */}
                    <div className="w-16 h-20 bg-[#e8dcc8] border border-text-dark rounded overflow-hidden flex-shrink-0">
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Product Details */}
                    <div className="flex-1">
                      <h3 className="font-medium text-text-dark text-sm mb-1">{item.title}</h3>
                      <div className="flex items-center gap-4">
                        {/* Quantity Controls */}
                        <div className="flex items-center border border-gray-300 rounded">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className={`px-2 py-1 text-text-gray hover:bg-gray-100 text-sm ${item.quantity <= 1 ? 'opacity-40 cursor-not-allowed hover:bg-transparent' : ''}`}
                            disabled={item.quantity <= 1}
                          >
                            -
                          </button>
                          <span className="px-3 py-1 text-sm">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="px-2 py-1 text-text-gray hover:bg-gray-100 text-sm"
                          >
                            +
                          </button>
                        </div>
                        
                        {/* Remove Button */}
                        <button 
                          onClick={() => removeItem(item.id)}
                          className="text-price-red text-xs hover:underline"
                        >
                          {t('cart.remove')}
                        </button>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="text-right">
                      <p className="font-bold text-text-dark">${item.price.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          </div>

          {/* Order Summary Section */}
          <div className="lg:w-1/3">
            {/* Continue Shopping */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-gray">{t('cart.continueShopping')}</span>
                <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-bold text-text-dark mb-4">{t('cart.orderSummary')}</h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-text-gray">{t('cart.subtotal')}</span>
                  <span className="text-text-dark">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-gray">{t('cart.tax')}</span>
                  <span className="text-text-dark">${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-gray">{t('cart.shipping')}</span>
                  <span className="text-green-600">{t('cart.free')}</span>
                </div>
                <hr className="border-gray-200" />
                <div className="flex justify-between font-bold">
                  <span className="text-text-dark">{t('cart.total')}</span>
                  <span className="text-gold text-lg">${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Promo Code */}
              <div className="mb-6">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder={t('cart.discountPlaceholder')}
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-gold"
                  />
                  <button className="px-4 py-2 bg-gold text-white text-sm rounded hover:bg-yellow-600 transition-colors">
                    {t('cart.apply')}
                  </button>
                </div>
              </div>

              {/* Checkout Button */}
              <Link 
                to="/checkout"
                className="w-full bg-brown text-cream py-3 rounded-md text-sm font-medium uppercase tracking-wide text-center block hover:bg-brown-dark transition-colors"
              >
                🛒 {t('cart.proceed')}
              </Link>
              
              <p className="text-xs text-text-gray text-center mt-3">{t('cart.secureNote')}</p>
            </div>
          </div>
        </div>
      </div>
      <QuickContact />
    </div>
  );
};

export default CartPage;
